/**
 * Conversion Log tab — view, dispatch, and retry conversion events.
 */

import { useEffect, useState, useCallback } from 'react'
import { ChannelBadge } from '@/components/marketing/ChannelBadge'
import {
  Card,
  CardContent,
  Button,
  Badge,
  Spinner,
} from '@/components/ui'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui'
import {
  RefreshCw,
  Send,
  RotateCcw,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react'
import { useMarketing } from '@/hooks/useMarketing'
import { useToast } from '@/components/ui/Toast'
import { formatMarketingCents } from '@/components/marketing/format'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString()
}


export function ConversionLog() {
  const {
    fetchStatus,
    fetchConversions,
    dispatchConversions,
    retryConversions,
    hasAccount,
    conversionEvents,
    conversionTotal,
  } = useMarketing()
  const { addToast } = useToast()

  const [loading, setLoading] = useState(true)
  const [dispatching, setDispatching] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const status = await fetchStatus()
      if (status.hasAccount) {
        await fetchConversions(statusFilter === 'all' ? undefined : statusFilter)
      }
    } catch {
      addToast({ title: 'Error', description: 'Failed to load conversions', type: 'error' })
    } finally {
      setLoading(false)
    }
  }, [fetchStatus, fetchConversions, statusFilter, addToast])

  useEffect(() => {
    load()
  }, [load])

  const handleDispatch = async () => {
    setDispatching(true)
    try {
      const count = await dispatchConversions()
      addToast({ title: 'Dispatched', description: `${count} events sent to Meta`, type: 'success' })
      await load()
    } catch {
      addToast({ title: 'Error', description: 'Dispatch failed', type: 'error' })
    } finally {
      setDispatching(false)
    }
  }

  const handleRetry = async () => {
    setRetrying(true)
    try {
      const count = await retryConversions()
      addToast({ title: 'Reset', description: `${count} events reset for retry`, type: 'success' })
      await load()
    } catch {
      addToast({ title: 'Error', description: 'Retry failed', type: 'error' })
    } finally {
      setRetrying(false)
    }
  }

  const statusIcon = (status: string) => {
    switch (status) {
      case 'sent': return <CheckCircle className="w-4 h-4 text-success" />
      case 'pending': return <Clock className="w-4 h-4 text-warning" />
      case 'failed': return <XCircle className="w-4 h-4 text-destructive" />
      case 'expired': return <AlertTriangle className="w-4 h-4 text-muted-foreground" />
      default: return <Clock className="w-4 h-4 text-muted-foreground" />
    }
  }

  const pendingCount = conversionEvents.filter((e) => e.status === 'pending').length
  const failedCount = conversionEvents.filter((e) => e.status === 'failed').length

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!hasAccount) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No Meta Ad Account connected. Go to <strong>Settings</strong> to connect.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">{conversionTotal} total events</span>
        </div>
        <div className="flex gap-2">
          {failedCount > 0 && (
            <Button variant="secondary" size="sm" onClick={handleRetry} disabled={retrying}>
              {retrying ? <Spinner size="sm" className="mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Retry Failed ({failedCount})
            </Button>
          )}
          {pendingCount > 0 && (
            <Button variant="secondary" size="sm" onClick={handleDispatch} disabled={dispatching}>
              {dispatching ? <Spinner size="sm" className="mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Dispatch ({pendingCount})
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={load}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Event list */}
      {conversionEvents.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No conversion events{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.
          </CardContent>
        </Card>
      ) : (
        <div className="max-h-[65vh] overflow-y-auto space-y-2 pr-1">
          {conversionEvents.map((event) => (
            <Card key={event.id}>
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {statusIcon(event.status)}
                    <div>
                      <p className="font-medium text-sm flex items-center gap-1.5">
                        <ChannelBadge channel={event.channel} showLabel={false} />
                        {event.eventName}
                        {event.value != null && (
                          <span className="text-success ml-2">{formatMarketingCents(event.value, event.currency)}</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(event.eventTime)}
                        {event.ctwaClid && <span className="ml-2">CTWA: {event.ctwaClid.slice(0, 12)}...</span>}
                        {event.retryCount > 0 && <span className="ml-2">Retries: {event.retryCount}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {event.errorMessage && (
                      <span className="text-xs text-destructive max-w-[200px] truncate" title={event.errorMessage}>
                        {event.errorMessage}
                      </span>
                    )}
                    <Badge
                      variant={
                        event.status === 'sent' ? 'default' :
                        event.status === 'failed' ? 'destructive' :
                        'secondary'
                      }
                    >
                      {event.status}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
